import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { ShieldCheck, Clock, AlertTriangle, Info } from 'lucide-react';

// Public "License Verified by INASW" badge for a social worker profile.
// Shows the green verified state only when the backend deems it publicly verified.
const VISUAL = {
  green: { ring: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck, label: 'License Verified by INASW' },
  yellow: { ring: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock, label: 'Renewal due soon' },
  gray: { ring: 'bg-gray-50 text-gray-600 border-gray-200', icon: Clock, label: 'Verification pending' },
  red: { ring: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle, label: 'Not currently verified' },
};

const LicenseVerifiedBadge = ({ userId, compact = false }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    if (!userId) return;
    axios.get(`${API_URL}/license-verification/${userId}`)
      .then((res) => { if (active) { setData(res.data); setLoaded(true); } })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [userId]);

  if (!loaded || !data?.has_verification) return null;

  const cfg = VISUAL[data.visual_status] || VISUAL.gray;
  const Icon = cfg.icon;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.ring}`} data-testid="license-badge-compact">
        <Icon className="w-3 h-3" />
        {data.public_verified ? 'Verified' : cfg.label}
      </span>
    );
  }

  return (
    <div className={`rounded-xl border p-4 ${cfg.ring}`} data-testid="license-verified-badge">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${data.public_verified ? 'bg-emerald-600' : 'bg-white/60'}`}>
          <Icon className={`w-5 h-5 ${data.public_verified ? 'text-white' : 'text-current'}`} />
        </div>
        <span className="font-semibold">{data.public_verified ? 'License Verified by INASW' : cfg.label}</span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-gray-700">
        {data.license_type && (<><dt className="text-gray-500">License</dt><dd className="font-medium">{data.license_type}</dd></>)}
        {data.state && (<><dt className="text-gray-500">State</dt><dd className="font-medium">{data.state}</dd></>)}
        {data.license_status && (<><dt className="text-gray-500">Status</dt><dd className="font-medium capitalize">{data.license_status}</dd></>)}
        {data.license_number && (<><dt className="text-gray-500">Number</dt><dd className="font-mono">{data.license_number}</dd></>)}
        {data.original_issue_date && (<><dt className="text-gray-500">Issued</dt><dd>{String(data.original_issue_date).slice(0,10)}</dd></>)}
        {data.expiration_date && (<><dt className="text-gray-500">Expires</dt><dd>{String(data.expiration_date).slice(0,10)}</dd></>)}
        {data.verified_at && (<><dt className="text-gray-500">Last verified</dt><dd>{String(data.verified_at).slice(0,10)}</dd></>)}
        {data.official_source_name && (<><dt className="text-gray-500">Source</dt><dd>{data.official_source_name}</dd></>)}
      </dl>
      <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500 leading-relaxed">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        {data.disclaimer}
      </p>
    </div>
  );
};

export default LicenseVerifiedBadge;
